import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, CreditCard, ToggleLeft, ToggleRight, Save, MessageCircle, Loader2 } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { PageSpinner } from '../../components/PageSpinner';
import { Pagination } from '../../components/Pagination';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';
const ITEMS_PER_PAGE = 15;

export default function AdminMediosPago() {
  const navigate = useNavigate();
  const [settings, setSettings] = useState({
    bold_enabled: true,
    flow_enabled: true,
    manual_enabled: true
  });
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [updatingStatusId, setUpdatingStatusId] = useState(null);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const token = localStorage.getItem('konemed_admin_token');
      const headers = { Authorization: `Bearer ${token}` };

      const [settingsRes, txnRes] = await Promise.all([
        axios.get(`${API_URL}/admin/payment-settings`, { headers }),
        axios.get(`${API_URL}/admin/transactions`, { headers })
      ]);

      setSettings(settingsRes.data);
      setTransactions(txnRes.data || []);
    } catch (error) {
      console.error('Error fetching data:', error);
      toast.error('Error al cargar configuración');
    } finally {
      setLoading(false);
    }
  };

  const handleStatusChange = async (txn, newStatus) => {
    if (newStatus === txn.status) return;

    const confirmMsg = newStatus === 'paid'
      ? 'Esto marcará la transacción como pagada y activará/renovará la membresía del centro como si el pago se hubiera confirmado automáticamente. ¿Continuar?'
      : `¿Cambiar el estado de esta transacción a "${statusLabels[newStatus] || newStatus}"?`;
    if (!window.confirm(confirmMsg)) return;

    setUpdatingStatusId(txn.id);
    try {
      const token = localStorage.getItem('konemed_admin_token');
      await axios.put(`${API_URL}/admin/transactions/${txn.id}/status`, { status: newStatus }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Estado de la transacción actualizado');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al actualizar el estado');
    } finally {
      setUpdatingStatusId(null);
    }
  };

  const handleToggle = (field) => {
    setSettings(prev => ({ ...prev, [field]: !prev[field] }));
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const token = localStorage.getItem('konemed_admin_token');
      await axios.put(`${API_URL}/admin/payment-settings`, settings, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Configuración guardada exitosamente');
    } catch (error) {
      toast.error('Error al guardar configuración');
    } finally {
      setSaving(false);
    }
  };

  const statusColors = {
    paid: 'bg-emerald-100 text-emerald-700',
    processing: 'bg-blue-100 text-blue-700',
    pending: 'bg-amber-100 text-amber-700',
    rejected: 'bg-red-100 text-red-700',
    cancelled: 'bg-slate-100 text-slate-600',
    expired: 'bg-slate-100 text-slate-500'
  };

  const statusLabels = {
    paid: 'Pagado',
    processing: 'Procesando',
    pending: 'Pendiente',
    rejected: 'Rechazado',
    cancelled: 'Cancelado',
    expired: 'Expirado'
  };

  const paymentMethods = [
    {
      id: 'bold_enabled',
      name: 'Bold.co',
      description: 'Pasarela de pagos para Colombia y otros países. Acepta tarjetas de crédito/débito, PSE, Nequi y Bancolombia.',
      icon: CreditCard,
      color: 'violet',
      countries: 'Colombia, Otros países'
    },
    {
      id: 'flow_enabled',
      name: 'Flow.cl',
      description: 'Pasarela de pagos para Chile. Acepta tarjetas de crédito/débito, Webpay y otros medios locales.',
      icon: CreditCard,
      color: 'blue',
      countries: 'Chile'
    },
    {
      id: 'manual_enabled',
      name: 'Pago Manual (WhatsApp)',
      description: 'Permite a los clientes contactar por WhatsApp para coordinar el pago de forma manual.',
      icon: MessageCircle,
      color: 'emerald',
      countries: 'Todos los países'
    }
  ];

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <PageSpinner />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      <div className="max-w-5xl mx-auto p-4 lg:p-8">
        <button
          onClick={() => navigate('/admin/dashboard')}
          className="flex items-center gap-2 text-slate-600 hover:text-slate-900 mb-6 transition-colors"
          data-testid="back-to-dashboard"
        >
          <ArrowLeft size={20} />
          Volver al Panel
        </button>

        <div className="mb-8">
          <h1 className="text-4xl font-bold text-slate-900 mb-2" data-testid="medios-pago-title">Medios de Pago</h1>
          <p className="text-slate-600">Configure los métodos de pago disponibles para las clínicas</p>
        </div>

        {/* Payment Methods Configuration */}
        <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden mb-8">
          <div className="bg-gradient-to-r from-violet-500 to-indigo-500 px-6 py-4">
            <div className="flex items-center gap-3">
              <CreditCard className="text-white" size={24} />
              <h2 className="text-xl font-bold text-white">Configuración de Métodos de Pago</h2>
            </div>
          </div>
          <div className="p-6 space-y-4">
            {paymentMethods.map((method) => {
              const Icon = method.icon;
              const isEnabled = settings[method.id];
              return (
                <div
                  key={method.id}
                  className={`flex items-center justify-between p-5 rounded-xl border-2 transition-all ${
                    isEnabled ? 'border-violet-200 bg-violet-50/30' : 'border-slate-200 bg-slate-50'
                  }`}
                  data-testid={`payment-method-config-${method.id}`}
                >
                  <div className="flex items-center gap-4">
                    <div className={`p-3 rounded-lg bg-${method.color}-100`}>
                      <Icon className={`text-${method.color}-600`} size={24} />
                    </div>
                    <div>
                      <h3 className="font-bold text-slate-900">{method.name}</h3>
                      <p className="text-sm text-slate-500 mt-1">{method.description}</p>
                      <p className="text-xs text-slate-400 mt-1">Países: {method.countries}</p>
                    </div>
                  </div>
                  <button
                    onClick={() => handleToggle(method.id)}
                    className="flex-shrink-0 ml-4"
                    data-testid={`toggle-${method.id}`}
                  >
                    {isEnabled ? (
                      <ToggleRight className="text-violet-600" size={40} />
                    ) : (
                      <ToggleLeft className="text-slate-400" size={40} />
                    )}
                  </button>
                </div>
              );
            })}
          </div>
          <div className="px-6 pb-6">
            <button
              onClick={handleSave}
              disabled={saving}
              className="flex items-center gap-2 px-6 py-3 bg-violet-600 hover:bg-violet-700 text-white font-bold rounded-xl transition-colors disabled:opacity-50"
              data-testid="save-payment-settings"
            >
              {saving ? <Loader2 className="animate-spin" size={20} /> : <Save size={20} />}
              {saving ? 'Guardando...' : 'Guardar Cambios'}
            </button>
          </div>
        </div>

        {/* Recent Transactions */}
        <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
          <div className="bg-gradient-to-r from-amber-500 to-orange-500 px-6 py-4">
            <div className="flex items-center gap-3">
              <CreditCard className="text-white" size={24} />
              <h2 className="text-xl font-bold text-white">Transacciones Recientes</h2>
            </div>
          </div>
          <div className="p-6">
            {transactions.length === 0 ? (
              <p className="text-slate-500 text-center py-8">No hay transacciones registradas</p>
            ) : (
              <>
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-slate-200">
                      <th className="text-left py-3 px-2 text-xs font-semibold text-slate-500 uppercase">Empresa</th>
                      <th className="text-left py-3 px-2 text-xs font-semibold text-slate-500 uppercase">Gateway</th>
                      <th className="text-left py-3 px-2 text-xs font-semibold text-slate-500 uppercase">Monto</th>
                      <th className="text-left py-3 px-2 text-xs font-semibold text-slate-500 uppercase">Estado</th>
                      <th className="text-left py-3 px-2 text-xs font-semibold text-slate-500 uppercase">Fecha</th>
                    </tr>
                  </thead>
                  <tbody>
                    {transactions.slice((currentPage - 1) * ITEMS_PER_PAGE, currentPage * ITEMS_PER_PAGE).map((txn) => (
                      <tr key={txn.id} className="border-b border-slate-100 hover:bg-slate-50" data-testid={`admin-txn-${txn.id}`}>
                        <td className="py-3 px-2">
                          <p className="text-sm font-medium text-slate-900">{txn.company_name || 'N/A'}</p>
                          <p className="text-xs text-slate-500">{txn.company_code || ''}</p>
                        </td>
                        <td className="py-3 px-2">
                          <span className="text-sm font-medium">
                            {txn.gateway === 'bold' ? 'Bold.co' : txn.gateway === 'flow' ? 'Flow.cl' : 'Manual'}
                          </span>
                        </td>
                        <td className="py-3 px-2">
                          <span className="text-sm font-bold text-slate-900">
                            ${new Intl.NumberFormat('es-CO').format(txn.amount || 0)} {txn.currency}
                          </span>
                        </td>
                        <td className="py-3 px-2">
                          <select
                            value={txn.status}
                            disabled={updatingStatusId === txn.id}
                            onChange={(e) => handleStatusChange(txn, e.target.value)}
                            className={`px-2 py-1 rounded text-xs font-semibold border-0 cursor-pointer disabled:opacity-50 disabled:cursor-wait ${statusColors[txn.status] || 'bg-slate-100 text-slate-600'}`}
                            data-testid={`txn-status-select-${txn.id}`}
                          >
                            {Object.entries(statusLabels).map(([value, label]) => (
                              <option key={value} value={value}>{label}</option>
                            ))}
                          </select>
                        </td>
                        <td className="py-3 px-2 text-sm text-slate-500">
                          {txn.created_at ? new Date(txn.created_at).toLocaleDateString('es-CO', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : 'N/A'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <div className="px-6 py-4">
                <Pagination currentPage={currentPage} totalPages={Math.ceil(transactions.length / ITEMS_PER_PAGE)} onPageChange={setCurrentPage} />
              </div>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
